/**
 * Role bands on "היום שלי" (D-163): "הצוות שלי" (Team Lead), "הלקוחות שלי"
 * (Account Manager), "הפרויקטים שלי" (Project Manager). The numbers come from
 * GET /dashboard/role-bands, which fills a band only for the relationship the
 * caller holds. The OTE manager keeps the general team band (RoleBand/TeamBand).
 * Each cell leads to a list that exists.
 */
export interface RoleBandsData {
  team: null | { members: number; present: number; active_timers: number; overdue_tasks: number };
  clients: null | { new_portal_tickets: number; overdue_debts: number; banks_over_threshold: number };
  projects: null | { milestones_due_or_late: number; projects_at_risk: number };
  /** D-347 §1 - the OTE manager: banks over their threshold across all customers (null otherwise). */
  banks_over_all?: number | null;
}

export interface BandCell {
  key: string;
  value: number;
  /** Total shown as "value / total" (present of members). */
  of?: number;
  to?: string;
  /** A non-zero value that is a problem (tinted). */
  warn?: boolean;
  /** D-347 §1: the whole cell (label + number) is the link, not only the number. */
  wholeLink?: boolean;
}

export interface Band {
  id: 'team' | 'clients' | 'projects' | 'banksAll';
  cells: BandCell[];
}

/** D-347 §1 - the engagements list filtered to the hour banks over their threshold. */
export const BANKS_OVER_LINK = '/engagements?banks_over=1';

/** D-347 §1 - the OTE manager's own cell: banks over the threshold across ALL customers. */
export function allBanksBand(data: RoleBandsData | null | undefined): Band | null {
  if (data?.banks_over_all == null) return null;
  return { id: 'banksAll', cells: [{ key: 'banksOver', value: data.banks_over_all, to: BANKS_OVER_LINK, warn: data.banks_over_all > 0, wholeLink: true }] };
}

export function bandsFrom(data: RoleBandsData | null | undefined): Band[] {
  if (!data) return [];
  const bands: Band[] = [];
  if (data.team) {
    const t = data.team;
    bands.push({
      id: 'team',
      cells: [
        { key: 'present', value: t.present, of: t.members },
        { key: 'timers', value: t.active_timers },
        { key: 'overdueTasks', value: t.overdue_tasks, to: '/tasks?due=overdue&from=home', warn: t.overdue_tasks > 0 },
      ],
    });
  }
  if (data.clients) {
    const c = data.clients;
    bands.push({
      id: 'clients',
      cells: [
        { key: 'newPortalTickets', value: c.new_portal_tickets, to: '/tickets?scope=to_me&active=true&from=home', warn: c.new_portal_tickets > 0 },
        { key: 'overdueDebts', value: c.overdue_debts, to: '/obligations?debt=true', warn: c.overdue_debts > 0 },
        { key: 'banksOver', value: c.banks_over_threshold, to: BANKS_OVER_LINK, warn: c.banks_over_threshold > 0, wholeLink: true },
      ],
    });
  }
  if (data.projects) {
    const p = data.projects;
    bands.push({
      id: 'projects',
      cells: [
        { key: 'milestonesDue', value: p.milestones_due_or_late, to: '/milestones', warn: p.milestones_due_or_late > 0 },
        { key: 'projectsAtRisk', value: p.projects_at_risk, to: '/projects', warn: p.projects_at_risk > 0 },
      ],
    });
  }
  return bands;
}
